/**
 * ExplodedViewController - Controlador de Vistas Explodidas Técnicas SVG & Balões ISO 128
 * Domínio: Manutenção Honda ADV 150 (Projeto K0W / Bloco KF43E)
 * Padrão Concessionária Honda Dream & Filosofia Fábio Akita
 */

import { ADV150_CANONICAL_PARTS, type CatalogPartItem } from "../data/catalog.js";

export interface ExplodedViewMeta {
  id: string;
  subsystemId: string;
  title: string;
  svgFile: string;
  animatedSvgFile?: string;
  description: string;
  canonicalTorques: string[];
  keyTools: string[];
}

export const ADV150_EXPLODED_DIAGRAMS: ReadonlyArray<ExplodedViewMeta> = [
  {
    id: "diag-cvt-primario",
    subsystemId: "cvt",
    title: "Variador Primário, Roletes 21g & Virabrequim",
    svgFile: "./assets/images/pecas/cvt-variador-polias-explodida.svg",
    animatedSvgFile: "./assets/images/pecas/cvt-variador-polias-explodida.animated.svg",
    description:
      "Vista axial explodida da ponta do virabrequim M14, polia móvel, 6 roletes a seco, rampa com slide pieces e ventilador a 59 N.m.",
    canonicalTorques: ["Porca M14 Virabrequim: 59 N.m (6,0 kgf.m)"],
    keyTools: ["Fixador em Y Honda 07725-0030000", "Torquímetro", "Soquete 22 mm"],
  },
  {
    id: "diag-cvt-secundario",
    subsystemId: "cvt",
    title: "Polia Secundária, Embreagem & Torque Cam",
    svgFile: "./assets/images/pecas/cvt-secundaria-embreagem-explodida.svg",
    description:
      "Explosão da embreagem centrífuga com mola de contraste de 50 kgf, canaletas helicoidais com graxa de poliureia e campana de 125 mm.",
    canonicalTorques: ["Campana M12: 49 N.m", "Porca M28 Mola: 54 N.m"],
    keyTools: ["Compressor de Mola 07LME-GZ40201", "Soquete Plano 39 mm 07GMA-KS40100"],
  },
  {
    id: "diag-cvt-sinking",
    subsystemId: "cvt",
    title: "Protocolo de Afundamento da Correia (Belt Sinking)",
    svgFile: "./assets/images/pecas/cvt-afundamento-belt-sinking.svg",
    description:
      "Procedimento obrigatório de folga frontal da correia Kevlar prevenindo mordedura na polia primária e quebra do virabrequim.",
    canonicalTorques: ["Torque seguro: 59 N.m sem esmagar correia"],
    keyTools: ["Inspeção visual direta com as duas mãos"],
  },
  {
    id: "diag-motor-lubrificacao",
    subsystemId: "motor",
    title: "Cárter eSP, Bujão M12 & Peneira de Óleo",
    svgFile: "./assets/images/pecas/motor-tela-oleo-bujoes.svg",
    description:
      "Vista de drenagem do cárter (800 ml 10W-30 MB) com bujão M12 e tela filtrante M30 com mola cônica e O-ring.",
    canonicalTorques: ["Bujão M12: 24 N.m", "Tampa Peneira M30: 20 N.m"],
    keyTools: ["Soquetes 12 mm e 17 mm", "Arruela esmagamento 12 mm nova"],
  },
  {
    id: "diag-motor-valvulas",
    subsystemId: "motor",
    title: "Sincronismo PMS, Comando OHC & Válvulas < 35°C",
    svgFile: "./assets/images/pecas/motor-sincronismo-pms-valvulas.svg",
    description:
      "Ponto Morto Superior na marca T do rotor e regulagem centesimal a frio (Adm: 0,10 mm / Esc: 0,24 mm) a 10 N.m.",
    canonicalTorques: ["Tampa Cabeçote: 10 N.m", "Contraporcas M5: 10 N.m"],
    keyTools: ["Cálibre de Lâminas Centesimal", "Chave Estria 9 mm"],
  },
  {
    id: "diag-motor-vela",
    subsystemId: "motor",
    title: "Vela de Ignição Especial NGK MR8K-9",
    svgFile: "./assets/images/pecas/motor-vela-ngk-mr8k9-31918-k97-t01.svg",
    description:
      "Geometria milimétrica da vela M10 x 1,0 de grau térmico 8 com gap calibrado de 0,80 a 0,90 mm a 16 N.m.",
    canonicalTorques: ["Vela de Ignição: 16 N.m (1,6 kgf.m)"],
    keyTools: ["Chave de Vela Articulada 16 mm com retentor"],
  },
  {
    id: "diag-arrefecimento",
    subsystemId: "motor",
    title: "Radiador Lateral, Bomba d'Água & Tampa 108 kPa",
    svgFile: "./assets/images/pecas/arrefecimento-radiador-tampa-108kpa.svg",
    description:
      "Esquema de fluxo de refrigeração eSP com Pro Coolant OAT 50/50, dreno M6 na bomba a 10 N.m e tampa 108 kPa.",
    canonicalTorques: ["Dreno Bomba d'Água: 10 N.m", "Capa Defletora: 9 N.m"],
    keyTools: ["Arruela cobre 6,5 mm nova", "Honda Pro Coolant Tipo 2"],
  },
  {
    id: "diag-pgmfi-tbi",
    subsystemId: "pgmfi",
    title: "Corpo de Borboleta TBI Keihin 26mm com Película MoS2",
    svgFile: "./assets/images/pecas/pgmfi-tbi-keihin-mos2.svg",
    description:
      "Corpo de aceleração monocorpo com película de bissulfeto de molibdênio, bico injetor 6 furos e sensor unit 3-em-1.",
    canonicalTorques: ["Coletor TBI: 10 N.m", "Válvula IACV: 2,1 N.m"],
    keyTools: ["Álcool Isopropílico 99%", "Proibido CAR 80 e raspagem"],
  },
  {
    id: "diag-freios-nissin",
    subsystemId: "freios",
    title: "Cálipers Nissin, Pastilhas Sinterizadas & Discos Wave",
    svgFile: "./assets/images/pecas/freio-pastilhas-nissin-diant-tras.svg",
    description:
      "Geometria dos cálipers Nissin dianteiro (2 pistões) e traseiro (1 pistão) com pastilhas de limite 1,0 mm e discos Wave 3,5 mm.",
    canonicalTorques: ["Cáliper Diant: 30 N.m", "Cáliper Tras: 22 N.m", "Discos Wave: 42 N.m"],
    keyTools: ["Graxa de Silicone Shin-Etsu G-40M pura", "Trava química azul"],
  },
  {
    id: "diag-freios-abs",
    subsystemId: "freios",
    title: "Circuito Hidráulico do ABS Monocanal & Regra dos 30%",
    svgFile: "./assets/images/pecas/freio-abs-circuito-blindagem.svg",
    description:
      "Circuito de blindagem do modulador ABS Nissin com sangria contínua por deslocamento sem esvaziamento do reservatório.",
    canonicalTorques: ["Válvula Sangria: 5,4 N.m", "Banjos M10: 34 N.m"],
    keyTools: ["Chave JIS #2 Vessel", "Torquímetro precisão baixa escala"],
  },
  {
    id: "diag-suspensao-showa",
    subsystemId: "suspensao",
    title: "Suspensões Showa: Garfo 130mm & Amortecedores Piggyback",
    svgFile: "./assets/images/pecas/suspensao-showa-piggyback.svg",
    description:
      "Arquitetura dos amortecedores duplos Showa Piggyback com reservatório de nitrogênio e garfo dianteiro Showa 31 mm.",
    canonicalTorques: [
      "Amortecedor Sup: 40 N.m",
      "Amortecedor Inf: 27 N.m",
      "Eixo Traseiro: 118 N.m",
    ],
    keyTools: ["Soquete 24 mm de alta escala", "Soquete 14 mm"],
  },
  {
    id: "diag-eletrica-smartkey",
    subsystemId: "eletrica",
    title: "Bateria Selada VRLA YTZ6V & Transmissor Smart Key",
    svgFile: "./assets/images/pecas/eletrica-bateria-ytz6v-smartkey.svg",
    description:
      "Verificação de tensão da bateria YTZ6V (≥ 12,8V / partida ≥ 10,2V) e polaridade da bateria moeda CR2032 (+) para baixo.",
    canonicalTorques: ["Aperto manual dos bornes"],
    keyTools: ["Multímetro digital", "Espátula plástica de abertura"],
  },
];

export class ExplodedViewController {
  private activeDiagramId: string;
  private readonly diagrams = ADV150_EXPLODED_DIAGRAMS;
  private readonly parts = ADV150_CANONICAL_PARTS;

  constructor(initialDiagramId = "diag-cvt-primario") {
    this.activeDiagramId = initialDiagramId;
  }

  public getActiveDiagram(): ExplodedViewMeta {
    const found = this.diagrams.find((d) => d.id === this.activeDiagramId);
    if (found) {
      return found;
    }
    const fallback = this.diagrams[0];
    if (fallback) {
      return fallback;
    }
    throw new Error("Nenhum diagrama de vista explodida disponível.");
  }

  public getAllDiagrams(): ReadonlyArray<ExplodedViewMeta> {
    return this.diagrams;
  }

  public setDiagram(diagramId: string): ExplodedViewMeta {
    const found = this.diagrams.find((d) => d.id === diagramId);
    if (found) {
      this.activeDiagramId = diagramId;
      return found;
    }
    return this.getActiveDiagram();
  }

  public getPartsForDiagram(svgFile: string): CatalogPartItem[] {
    const filename = svgFile.split("/").pop();
    return this.parts.filter(
      (p) => p.explodedViewId && filename && p.explodedViewId.includes(filename),
    );
  }
}
